import { DodRunConfig } from '@/types/DodRun';
import { TabWizardRef } from '@byzzer/ui-components';
import React from 'react';

export type ExtractWizardStep = NonNullable<DodRunConfig['layout']['extractWizardStep']>;

/**
 * Maps component names to their corresponding step numbers (1-indexed).
 * This ensures accurate step tracking even when Apply conditions or other
 * changes are triggered before activeStepIndex updates in the ref.
 */
export const STEP_NAME_MAP: Record<string, ExtractWizardStep> = {
    'facts': 4,   // Facts step is step 4 (index 3)
    'layout': 5,  // Layout step is step 5 (index 4)
};

/**
 * Determines the wizard step number from a component name.
 * Prefers explicit name mapping to avoid stale ref values.
 * 
 * @param name - The component name from the event (e.g., 'facts', 'layout', 'filters')
 * @returns The step number (1-5) if name is mapped, undefined otherwise
 */
export function getStepFromName(name: string | undefined): ExtractWizardStep | undefined {
    return STEP_NAME_MAP[name as string] as ExtractWizardStep | undefined;
}

/**
 * Computes the maximum wizard step reached in the session.
 * wizardState tracks the highest step visited, not the currently visible step.
 * This ensures users don't lose progress when navigating backward.
 * 
 * @param previousWizardState - The previously recorded max-reached step (or undefined)
 * @param currentStep - The current step being navigated to (1-5)
 * @returns The max step reached, clamped to 1-5 range
 */
export function getMaxWizardState(
    previousWizardState: DodRunConfig['layout']['wizardState'] | undefined,
    currentStep: ExtractWizardStep
): ExtractWizardStep {
    // Calculate max, but ensure result stays within valid range (1-5)
    const maxStep = Math.max(previousWizardState ?? currentStep, currentStep);
    return Math.min(maxStep, 5) as ExtractWizardStep;
}

/**
 * Determines the current wizard step from the active step index in the wizard ref.
 * Clamps result to the valid 1-5 range.
 *
 * @param wizardRef - React ref to the TabWizard component
 * @returns The current step number (1-5)
 */
export function getActiveWizardStep(wizardRef: React.RefObject<TabWizardRef>): ExtractWizardStep {
    // Prefer active step name from wizard ref when available, because activeStepIndex can
    // be stale during nested updates (e.g., applying layout conditions).
    const stepFromActiveName = getStepFromName(wizardRef.current?.activeStep as string | undefined);
    if (stepFromActiveName !== undefined) {
        return stepFromActiveName;
    }

    const index = wizardRef.current?.activeStepIndex ?? 0;
    return Math.min(Math.max(index + 1, 1), 5) as ExtractWizardStep;
}
